// Single source of truth for the Explore page quiz + community grid.
// Ported from the approved prototype: docs/quiz/FIF Quiz v5.4.html
// `hero` is intentionally a placeholder label (not a photo) — real FIF photos
// get swapped into the <ImagePlaceholder> slots before launch.

import kismetHero from "../assets/img/explore/kismet2.png";
import oceanBeachHero from "../assets/img/explore/ocean-beach.jpg";
import atlantiqueHero from "../assets/img/explore/atlantique-hero.jpg";
import fairHarborHero from "../assets/img/explore/fair-harbor-hero.jpg";
import saltaireHero from "../assets/img/explore/saltaire-hero.jpg";
import seaviewHero from "../assets/img/explore/seaview-hero.jpg";
import oceanBayParkHero from "../assets/img/explore/ocean-bay-park-hero.jpg";
import activeBeachImg from "../assets/img/explore/active-beach.png";
import calmBeachImg from "../assets/img/explore/calm-beach.jpg";
import kidsPlayingImg from "../assets/img/explore/kids-playing.jpg";
import shoppingImg from "../assets/img/explore/shopping.png";
import sunkenForestImg from "../assets/img/explore/sunken-forest-2.jpg";
import lighthouseImg from "../assets/img/explore/lighthouse.jpg";
import diningImg from "../assets/img/explore/dining.jpg";
import coolerImg from "../assets/img/explore/beach-cooler.jpg";
import natureImg from "../assets/img/explore/sunken-forest-walk.png";
import parkingImg from "../assets/img/explore/car.png";
export const DESTINATIONS = {
  Kismet: {
    slug: "kismet",
    heroImage: kismetHero,
    hero: "Kismet bayfront / marina, ferry visible, late afternoon",
    tagline: "Where the island begins — and the day slows down",
    gridTagline: "Easy streets and lighthouse views.",
    amenitiesLine:
      "Ocean lifeguards · Bars & restaurants · Walk to lighthouse · Coffee · Shops",
    ferryFrom: "Bay Shore (Saltaire or Main Terminal)",
    amenities: [
      "✓ Ocean lifeguards",
      "✓ Bars & restaurants",
      "✓ Walk to lighthouse",
      "✓ Kid friendly",
      "✓ Market / grocery",
      "✓ Ice cream",
      "✓ Coolers welcome",
    ],
    noAmenities: [],
    headsUp: null,
    desc: 'Kismet is the westernmost community on Fire Island, and the one closest in spirit to the island\'s original soul. Known for its "let it be" attitude and Margaritaville vibe, it draws an easy mix of families, day-trippers, and longtime devotees who come for the wide, uncrowded beach, the walk to the Lighthouse, and the bayfront restaurants.',
    tips: [
      "Start the morning at Kismet Coffee before hitting the beach.",
      "The Kismet Inn is a Fire Island institution — order the baked clams and settle in at the marina.",
      "Browse The Red Wagon Emporium for unique Fire Island finds before you catch the ferry home.",
    ],
    captainsTip:
      "You can spot the Fire Island Lighthouse before you even dock in Kismet — keep an eye out on the upper deck.",
    itinerary: [
      {
        t: "Morning",
        h: "Ferry in + Kismet Coffee",
        d: "Step off the ferry and stop by Kismet Coffee, then walk 5 mins to the beach.",
      },
      {
        t: "Midday",
        h: "Swim and then walk to the Lighthouse",
        d: "Lifeguards on duty in season. Follow the shoreline west for a ~20-min walk to the Fire Island Lighthouse.",
      },
      {
        t: "Afternoon",
        h: "Baked clams at the Kismet Inn",
        d: "Marina crowd mixes with day-trippers in a way only Fire Island does.",
      },
      {
        t: "Late afternoon",
        h: "The Red Wagon Emporium to shop + evening ferry to mainland",
        d: "Browse The Red Wagon Emporium for unique Fire Island finds, then catch the evening ferry home.",
      },
    ],
    logistics: "Bay Shore, NY → Ferry to Kismet",
    logOpts: [
      {
        badge: "By car",
        text: "Check schedule for <strong>Saltaire</strong> or <strong>Main Terminal</strong>. Arrive early on summer weekends.",
      },
      {
        badge: "By LIRR",
        text: "LIRR to <strong>Bay Shore, NY</strong>. Walk about a mile, or catch <strong>David's Buses</strong> for a direct drop.",
      },
    ],
    links: [
      {
        src: "Everlastying",
        title: "Kismet Beach, Fire Island — Day Trip Guide from NYC",
        desc: "A firsthand guide covering the beach, the Lighthouse walk, and what to expect.",
        url: "https://www.everlastying.com/explore/kismet-beach-fire-island",
      },
      {
        src: "Fire Island Finder",
        title: "Kismet — Village Guide",
        desc: "Local guide to Kismet's restaurants, marina, beach, and year-round character.",
        url: "https://www.fireislandfinder.com/fire-island-villages/kismet/",
      },
      {
        src: "Hither & Thither",
        title: "Day Trip to Fire Island: Ideas, Tips and How We Made It Work",
        desc: "A travel writer's narrative day trip that starts in Kismet.",
        url: "https://hitherandthither.net/daytrip-fire-island/",
      },
    ],
  },

  "Ocean Beach": {
    slug: "ocean-beach",
    heroImage: oceanBeachHero,
    hero: "Ocean Beach dock with ferry loading in the foreground.",
    tagline: "A century of summers, and still going strong",
    gridTagline: "The island's most popular destination.",
    amenitiesLine:
      "Ocean & bay lifeguards (no food/drink) · Shops · Restaurants · Market · Nightlife",
    ferryFrom: "Bay Shore (Main Terminal)",
    amenities: [
      "✓ Ocean lifeguards",
      "✓ Bay beach + lifeguards",
      "✓ Bars & restaurants",
      "✓ Public restrooms",
      "✓ Kid friendly",
      "✓ Market / grocery",
      "✓ Ice cream",
      "✓ Shops",
    ],
    noAmenities: ["✗ No coolers / outside food"],
    headsUp: {
      title: "Good to know before you go",
      text: 'Ocean Beach has a long tradition of strict local rules — locals affectionately call it "The Land of No." No eating and drinking on the beach is enforced. Plan to eat in town before or after the sand. The upside? The beaches here are immaculate and the town has lots to see and do.',
    },
    desc: "Ocean Beach was incorporated in 1921 and has been drawing people across the Great South Bay ever since. The Hollywood crowd came in the 40s and 50s — Marilyn Monroe, the Gershwins, Mel Brooks — and the energy hasn't let up. Bay Walk runs along the bayfront and is lined with restaurants, boutiques, bakeries, art galleries, and a food market.",
    tips: [
      "Rachel's Bakery is the morning move — get there early before the good stuff is gone.",
      "Matthew's Seafood is a waterfront spot for lunch or dinner — watch the boats come in while you eat.",
      "Pick up Unfriendly's ice cream on your way back to the ferry. The name is a running joke. The ice cream is not.",
    ],
    captainsTip:
      "Ocean Beach is a busy stop on the bay — on summer weekends, ferries fill up fast so be prepared to get cozy with your neighbor. Upper deck for the views on the crossing.",
    itinerary: [
      {
        t: "Morning",
        h: "Arrive + breakfast at Rachel's Bakery",
        d: "Step off the ferry onto Bay Walk and head straight to Rachel's. Grab a coffee and something fresh-baked before the day gets going.",
      },
      {
        t: "Midday",
        h: "Ocean swim + bay beach",
        d: "Head south to the ocean — lifeguards on duty in season. Cross back through town to the calmer bay beach for the afternoon. No food or drinks on either beach.",
      },
      {
        t: "Afternoon",
        h: "Lunch + wander Bay Walk",
        d: "Eat lunch, then wander the car-free streets, browse the shops, and take in a town that's been doing this for over a hundred years.",
      },
      {
        t: "Evening",
        h: "Dinner at Matthew's + Unfriendly's on the way out",
        d: "Waterfront table at Matthew's Seafood, watch the boats pull in. Then stop at Unfriendly's for ice cream and catch the evening ferry.",
      },
    ],
    logistics: "Bay Shore, NY → Ferry to Ocean Beach",
    logOpts: [
      {
        badge: "By car",
        text: "<strong>Main Terminal</strong>. Arrive early — lots fill quickly on summer weekends.",
      },
      {
        badge: "By LIRR",
        text: "LIRR to <strong>Bay Shore, NY</strong>. Walk about a mile, or take <strong>David's Buses</strong> for a direct connection.",
      },
    ],
    links: [
      {
        src: "Momma To Go",
        title: "A Day Trip to Fire Island",
        desc: "A family travel blogger's firsthand Ocean Beach guide.",
        url: "https://mommatogo.com/a-day-trip-to-fire-island/",
      },
      {
        src: "Hither & Thither",
        title: "Day Trip to Fire Island: Ideas, Tips and How We Made It Work",
        desc: "A narrative day trip ending in Ocean Beach — Matthew's Seafood and the ferry sunset.",
        url: "https://hitherandthither.net/daytrip-fire-island/",
      },
      {
        src: "AAA Northeast",
        title: "Things to Do on Fire Island",
        desc: "AAA's editorial guide to Fire Island destinations and day trip planning.",
        url: "https://magazine.northeast.aaa.com/daily/travel/day-trips/things-to-do-in-fire-island/",
      },
    ],
  },

  Atlantique: {
    slug: "atlantique",
    heroImage: atlantiqueHero,
    hero: "Wide open Atlantique beach with umbrellas, family with cooler in foreground",
    tagline: "Bring the cooler, claim your spot, stay all day",
    gridTagline: "Bring the cooler, claim your spot, stay all day.",
    amenitiesLine: "Ocean & bay lifeguards · Restrooms & showers · Food shack",
    ferryFrom: "Bay Shore (Clinton Ave)",
    amenities: [
      "✓ Ocean lifeguards",
      "✓ Bay beach + lifeguards",
      "✓ Coolers & outside food welcome",
      "✓ Restrooms + hot showers",
      "✓ Kid friendly",
      "✓ Snack bar + ice cream",
      "✓ BBQ areas",
    ],
    noAmenities: ["✗ No shops", "✗ No market / grocery"],
    headsUp: null,
    desc: "Founded in 1912, Atlantique sits at one of the narrower points on Fire Island — the walk from bay to ocean takes minutes, guided by sand dune boardwalks the whole way. Built around a 150-slip marina and The Shack — a mid-island bar and restaurant with cold drinks and live music on weekends. Also, the only town with showers available to the public.",
    tips: [
      "Bring a cooler — outside food and drinks are welcome on the beach here, unlike most Fire Island communities.",
      "BBQ grills and picnic areas are on-site — bring charcoal and make a full day of it.",
      "Hot showers on-site — a genuinely welcome find after a long day in the sand and surf.",
    ],
    captainsTip:
      "The Atlantique ferry departs from <strong>Clinton Ave/Marina</strong> in Bay Shore — not the main terminal. This is a budget-friendlier option — more parking, lower rates, and a relaxed departure.",
    itinerary: [
      {
        t: "Morning",
        h: "Park + ferry in",
        d: "Load up the cooler and wagon. (Or grab a to-go bite from Nicky's on the Bay.)",
      },
      {
        t: "Midday",
        h: "Sand dune boardwalks connect bay to ocean",
        d: "Eat on the beach — coolers welcome.",
      },
      {
        t: "Afternoon",
        h: "BBQ + The Shack",
        d: "Cold drinks, good food, live music on weekends.",
      },
      {
        t: "Late afternoon",
        h: "Hot shower + ferry home",
        d: "Or walk to Ocean Beach or Fair Harbor for dinner first.",
      },
    ],
    logistics:
      "99 Maple Ave or Clinton Ave Marina, Bay Shore → Ferry to Atlantique",
    logOpts: [
      {
        badge: "By car",
        text: "<strong>99 Maple Ave</strong> or <strong>Clinton Ave Marina</strong> parking lot, Bay Shore.",
      },
      {
        badge: "By LIRR",
        text: "LIRR to <strong>Bay Shore, NY</strong>. <strong>David's Buses</strong> to Atlantique.",
      },
    ],
    links: [
      {
        src: "Fire Island Finder",
        title: "Atlantique — Village Guide",
        desc: "A detailed local guide covering the marina, The Shack, beaches, and what makes Atlantique a draw for boaters and families alike.",
        url: "https://www.fireislandfinder.com/fire-island-villages/atlantique/",
      },
      {
        src: "Loving Long Island",
        title: "Atlantique, Fire Island",
        desc: "A firsthand account of spending time at Atlantique marina.",
        url: "http://www.loving-long-island.com/atlantique.html",
      },
      {
        src: "AAA Northeast",
        title: "Things to Do on Fire Island",
        desc: "AAA's editorial guide to Fire Island destinations and day trip planning.",
        url: "https://magazine.northeast.aaa.com/daily/travel/day-trips/things-to-do-in-fire-island/",
      },
    ],
  },

  "Fair Harbor": {
    slug: "fair-harbor",
    heroImage: fairHarborHero,
    hero: "Fair Harbor beach — towels and umbrellas, barefoot, midday sun",
    tagline: "Barefoot, bohemian, and built for long beach days",
    gridTagline: "Barefoot, bohemian, and built for long beach days.",
    amenitiesLine: "Ocean & bay lifeguards · Restaurant · Market",
    ferryFrom: "Bay Shore (Main Terminal)",
    amenities: [
      "✓ Ocean lifeguards",
      "✓ Bay beach + lifeguards",
      "✓ Bars & restaurants",
      "✓ Kid friendly",
      "✓ Market / grocery",
      "✓ Ice cream",
      "✓ Shops",
    ],
    noAmenities: [],
    headsUp: null,
    desc: "Fair Harbor is one of those places that gets under your skin. A barefoot community of about 350 homes, it has just enough of everything — a well-stocked market, a restaurant with bay views, an ice cream stand, a bike shop, and two lifeguarded beaches — without ever feeling like too much.",
    tips: [
      "Stock up at Pioneer Market — a well-stocked grocery steps from the ferry dock.",
      "Le Dock has bay views and doubles as a pizza takeout window — great for a beach picnic or a sit-down dinner watching the boats come in.",
      "Stay for sunset — locals and visitors gather at the dock every evening, sometimes with live music.",
    ],
    captainsTip:
      "Fair Harbor departs from the <strong>main terminal on Maple Avenue</strong> in Bay Shore — for a day trip, look for parking at the train station (up to 72 hours) and walk or take David's Buses to the terminal.",
    itinerary: [
      {
        t: "Morning",
        h: "Ferry in + Pioneer Market",
        d: "Breakfast supplies right across from the dock. Head to the beach (bay or ocean).",
      },
      {
        t: "Afternoon",
        h: "Lunch at Le Dock + explore town by foot or bike",
        d: "Rent bikes from Corliss and ride the boardwalks to nearby Kismet, Saltaire or Atlantique.",
      },
      {
        t: "Sunset",
        h: "Dock sunset + Unfriendly's on the way home",
        d: "The sunset over the Great South Bay draws a crowd at the dock every evening. Grab an ice cream from Unfriendly's for the ride home.",
      },
    ],
    logistics: "Maple Avenue Main Terminal, Bay Shore → Ferry to Fair Harbor",
    logOpts: [
      {
        badge: "By car",
        text: "<strong>Maple Avenue Main Terminal, Bay Shore</strong>. Allow extra time on busy summer weekends.",
      },
      {
        badge: "By LIRR",
        text: "LIRR to <strong>Bay Shore, NY</strong>. Walk about a mile to Main Terminal, or catch <strong>David's Buses</strong>.",
      },
    ],
    links: [
      {
        src: "Good City Living",
        title: "New York Daytrip: Fair Harbor, Fire Island",
        desc: "A family travel writer's firsthand guide.",
        url: "https://www.goodcityliving.com/exploring-blog/fair-harbor-fire-island",
      },
      {
        src: "Fire Island Finder",
        title: "Fair Harbor — Village Guide",
        desc: "A detailed local guide to Fair Harbor's bayfront, shops, beaches, and community vibe.",
        url: "https://www.fireislandfinder.com/fire-island-villages/fair-harbor/",
      },
      {
        src: "AAA Northeast",
        title: "Things to Do on Fire Island",
        desc: "AAA's editorial guide.",
        url: "https://magazine.northeast.aaa.com/daily/travel/day-trips/things-to-do-in-fire-island/",
      },
    ],
  },

  Saltaire: {
    slug: "saltaire",
    heroImage: saltaireHero,
    hero: "Saltaire tree-lined manicured boardwalk, early morning, no people",
    tagline: "Quiet, manicured, and completely unhurried",
    gridTagline: "Quiet, manicured, and completely unhurried.",
    amenitiesLine: "Ocean & bay lifeguards (no food/drink) · Market",
    ferryFrom: "Bay Shore (Saltaire terminal)",
    amenities: [
      "✓ Ocean lifeguards",
      "✓ Bay beach + lifeguards",
      "✓ Kid friendly",
      "✓ Public restrooms",
      "✓ Market / grocery",
      "✓ Walk to lighthouse",
    ],
    noAmenities: ["✗ No public bars / restaurants", "✗ No ice cream"],
    headsUp: {
      title: "Good to know before you go",
      text: "Saltaire is a private village — its yacht club is for members and guests only. The market, though, is well-stocked, and Kismet and Fair Harbor are both short walks away when you want a meal. Food/alcohol and dogs are not allowed on the beaches.",
    },
    desc: "Founded in 1911 and named after a village in Yorkshire, England, Saltaire is one of Fire Island's two incorporated villages. Tree-lined boardwalks, elegant beach cottages with distinctive arched windows, and a manicured bayfront give it a character all its own. Famous author Harper Lee spent time there.",
    tips: [
      "Stock up at the market before heading to the beach — the nearest restaurants are in Kismet or Fair Harbor, both a comfortable walk along the beach.",
      "The bay beach is calm and family-friendly — great for younger kids who aren't ready for ocean waves.",
      "The tree-lined boardwalks and 1940s Coffey houses with their distinctive arched windows are worth a slow wander through town.",
    ],
    captainsTip:
      "Saltaire is a quieter stop — grab the upper deck and enjoy the crossing. The late evening ferry from Fair Harbor stops at Saltaire, so you can wander between the two towns and still make your boat home.",
    itinerary: [
      {
        t: "Morning",
        h: "Ferry in + market stop",
        d: "Snacks and drinks at the market on Broadway. Ocean and bay beaches both have lifeguards on duty in season.",
      },
      {
        t: "Midday",
        h: "Beach morning + boardwalk wander",
        d: "Take a slow walk through Saltaire's tree-lined boardwalks.",
      },
      {
        t: "Afternoon",
        h: "Walk toward the Lighthouse through Kismet",
        d: "Stop for a meal in Kismet along the way.",
      },
      {
        t: "Evening",
        h: "Or head east to Fair Harbor + ferry home",
        d: "Le Dock for a waterfront table, sunset over the bay.",
      },
    ],
    logistics: "Bay Shore, NY → Ferry to Saltaire",
    logOpts: [
      {
        badge: "By car",
        text: "Park at the <strong>Bay Shore Ferry Terminal</strong>. Arrive early on summer weekends.",
      },
      {
        badge: "By LIRR",
        text: "LIRR to <strong>Bay Shore, NY</strong>. Walk about a mile, or take <strong>David's Buses</strong> to the ferry.",
      },
    ],
    links: [
      {
        src: "Fire Island Finder",
        title: "Saltaire — Village Guide",
        desc: "A detailed guide to Saltaire's architecture, community, beaches, and distinct character.",
        url: "https://www.fireislandfinder.com/fire-island-villages/saltaire/",
      },
      {
        src: "Mommy Poppins",
        title: "Visiting Fire Island with Kids",
        desc: "A family travel guide that highlights Saltaire among the island's quieter communities.",
        url: "https://mommypoppins.com/likids/long-island-staycations-fire-island",
      },
      {
        src: "AAA Northeast",
        title: "Things to Do on Fire Island",
        desc: "AAA's editorial guide.",
        url: "https://magazine.northeast.aaa.com/daily/travel/day-trips/things-to-do-in-fire-island/",
      },
    ],
  },

  "Ocean Bay Park": {
    slug: "ocean-bay-park",
    heroImage: oceanBayParkHero,
    hero: "Flynn's marina patio at golden hour, string lights, locals at the bar",
    tagline:
      "Small town, big personality — and a bar scene that earns its reputation",
    gridTagline: "Small town, big personality.",
    amenitiesLine: "Restaurants · Bars · Shops",
    ferryFrom: "Bay Shore (West Terminal)",
    amenities: ["✓ Bars & restaurants", "✓ Kid friendly", "✓ Shops"],
    noAmenities: ["✗ No ocean lifeguards", "✗ No restrooms on beach"],
    headsUp: {
      title: "Good to know before you go",
      text: "The beach has no lifeguards or restrooms on the sand. Plan ahead with kids. Restrooms are available in town. Ocean Beach is a short walk west if you need lifeguard coverage.",
    },
    desc: "Ocean Bay Park — OBP to everyone who loves it — is a small town with an outsized personality. About 350 homes, an eclectic mix of families, artists, young renters, and longtime regulars, and a bayfront scene that has been drawing people across the Great South Bay for decades.",
    tips: [
      "Flynn's is the anchor of OBP — waterfront dining, a marina, and a happy hour that draws people from across the bay.",
      "The Schooner Inn on the bay features live music and Reggae nights — a lively spot for an evening on the water.",
      "OBP is one stop east of Ocean Beach on the ferry route — easy to combine both towns in a single day.",
    ],
    captainsTip:
      "OBP is a short walk to Ocean Beach. Mix up your day and start in one and depart from the other. (Ferries leave from different terminals on the mainland — ask the crew for directions.)",
    itinerary: [
      {
        t: "Morning",
        h: "Ferry in + grab supplies in town",
        d: "No restrooms or lifeguards on the sand — grab everything you need before heading to the beach.",
      },
      {
        t: "Midday",
        h: "Beach morning",
        d: "If traveling with young children, walk west to Ocean Beach where lifeguards are on duty.",
      },
      {
        t: "Afternoon",
        h: "Flynn's on the marina",
        d: "Cold drinks, seafood, and views across the Great South Bay.",
      },
      {
        t: "Evening",
        h: "Dinner at The Schooner + evening ferry",
        d: "Dinner and live music as the sun goes down.",
      },
    ],
    logistics: "Maple Avenue Dock, Bay Shore → Ferry to Ocean Bay Park",
    logOpts: [
      {
        badge: "By car",
        text: "Park at <strong>Maple Avenue dock, Bay Shore</strong> — more parking and lower rates than the main terminal.",
      },
      {
        badge: "By LIRR",
        text: "LIRR to <strong>Bay Shore, NY</strong>, then walk or take <strong>David's Buses</strong> to the Maple Avenue dock.",
      },
    ],
    links: [
      {
        src: "Fire Island Finder",
        title: "Ocean Bay Park — Village Guide",
        desc: "A local guide to OBP's restaurants, bar scene, architecture, and community character.",
        url: "https://www.fireislandfinder.com/fire-island-villages/ocean-bay-park/",
      },
      {
        src: "Flynn's Fire Island",
        title: "Flynn's — OBP Waterfront Bar & Restaurant",
        desc: "A waterfront marina restaurant directly on the Great South Bay.",
        url: "https://www.flynnsfireislandny.com/",
      },
      {
        src: "AAA Northeast",
        title: "Things to Do on Fire Island",
        desc: "AAA's editorial guide.",
        url: "https://magazine.northeast.aaa.com/daily/travel/day-trips/things-to-do-in-fire-island/",
      },
    ],
  },

  Seaview: {
    slug: "seaview",
    heroImage: seaviewHero,
    hero: "Seaview lifeguarded bay beach with the children's wading pool, late morning",
    tagline: "Quietly tucked in — with two lively towns just a walk away",
    gridTagline: "Quietly tucked in — with two lively towns just a walk away.",
    amenitiesLine: "Ocean & bay lifeguards · Children's wading pool · Market",
    ferryFrom: "Bay Shore (West Terminal)",
    amenities: [
      "✓ Bay beach + lifeguards",
      "✓ Ocean beach",
      "✓ Kid friendly",
      "✓ Market / grocery",
      "✓ Children's wading pool",
    ],
    noAmenities: [],
    headsUp: null,
    desc: "Seaview is one of Fire Island's sleepiest communities, snugly wedged between Ocean Beach and Ocean Bay Park. It has a large, deep-water lifeguarded bay with a separate children's wading pool and swim instruction programs. The streets are paved with a distinctive brickwork pattern.",
    tips: [
      "The bay beach here is a standout — a large, deep-water lifeguarded swimming area with a separate children's wading pool.",
      "The market is right next to the ferry dock — stock up when you arrive.",
      "Ocean Beach is a short walk west and Ocean Bay Park a short walk east — easy to combine a quiet morning in Seaview with more activity when you're ready.",
    ],
    captainsTip:
      "Seaview is a quieter stop on the bay — you'll arrive to a calmer dock and a community that keeps to itself. Two active towns are right next door whenever you want them.",
    itinerary: [
      {
        t: "Morning",
        h: "Ferry in + market stop",
        d: "The market is steps from the ferry basin. Grab snacks and drinks, then head to the bay beach and children's wading pool.",
      },
      {
        t: "Midday",
        h: "Bay beach morning + ocean swim",
        d: "The lifeguarded bay is calm and welcoming. Cross to the ocean side for the afternoon if you want waves.",
      },
      {
        t: "Afternoon",
        h: "Wander the brickwork boardwalks",
        d: "Seaview's distinctive brick-paved walks and shaded tree-lined streets make for a pleasant afternoon stroll.",
      },
      {
        t: "Evening",
        h: "Walk to OB or OBP for dinner + ferry home",
        d: "Head west to Ocean Beach for dinner at Matthew's, or east to Ocean Bay Park for Flynn's.",
      },
    ],
    logistics: "Bay Shore, NY → Ferry to Seaview",
    logOpts: [
      {
        badge: "By car",
        text: "<strong>West Side Terminal</strong>. Arrive early on summer weekends.",
      },
      {
        badge: "By LIRR",
        text: "LIRR to <strong>Bay Shore, NY</strong>. Walk or take <strong>David's Buses</strong> to the West Side terminal.",
      },
    ],
    links: [
      {
        src: "Fire Island Finder",
        title: "Seaview — Village Guide",
        desc: "A local guide to Seaview's community, bay beach, brickwork walks, and quiet family character.",
        url: "https://www.fireislandfinder.com/fire-island-villages/seaview/",
      },
      {
        src: "FireIsland.com",
        title: "Seaview, Fire Island",
        desc: "Overview of Seaview's location and amenities.",
        url: "https://fireisland.com/beaches/seaview-fire-island/",
      },
      {
        src: "Mommy Poppins",
        title: "Visiting Fire Island with Kids",
        desc: "A family travel guide highlighting Seaview among kid-friendly communities.",
        url: "https://mommypoppins.com/likids/long-island-staycations-fire-island",
      },
    ],
  },
};

// Grid display order (matches the approved Explore copy, Section 3).
export const TOWN_ORDER = [
  "Kismet",
  "Saltaire",
  "Fair Harbor",
  "Atlantique",
  "Seaview",
  "Ocean Beach",
  "Ocean Bay Park",
];

// Step 1 image picker. `tags` drive scoreS1; `__surprise__` / `__parking__`
// are special flags. `label` doubles as the placeholder caption.
export const IMAGES = [
  {
    id: "lively",
    label: "A lively beach scene with lifeguards",
    tags: ["Kismet", "Ocean Beach", "Ocean Bay Park"],
    image: activeBeachImg,
  },
  {
    id: "quiet",
    label: "A peaceful, uncrowded stretch of beach",
    tags: ["Kismet", "Fair Harbor", "Atlantique", "Saltaire", "Seaview"],
    image: calmBeachImg,
  },
  {
    id: "family",
    label: "Kids splashing in calm, safe water",
    tags: ["Ocean Beach", "Fair Harbor", "Atlantique", "Saltaire"],
    image: kidsPlayingImg,
  },
  {
    id: "shopping",
    label: "Exploring shops and storefronts",
    tags: ["Kismet", "Ocean Beach", "Ocean Bay Park"],
    image: shoppingImg,
  },
  {
    id: "sightseeing",
    label: "Lighthouse, water taxi & hidden gems",
    tags: ["__surprise__"],
    image: lighthouseImg,
  },
  {
    id: "dining",
    label: "Food, drinks & dining out",
    tags: ["Kismet", "Ocean Beach", "Atlantique", "Ocean Bay Park"],
    image: diningImg,
  },
  {
    id: "cooler",
    label: "Bringing our own food & drinks",
    tags: ["Kismet", "Fair Harbor", "Atlantique", "Seaview", "Ocean Bay Park"],
    image: coolerImg,
  },
  {
    id: "nature",
    label: "Nature walks & boardwalk trails",
    tags: ["__surprise__"],
    image: natureImg,
  },
  {
    id: "parking",
    label: "Arriving by car — need parking info",
    tags: ["__parking__"],
    image: parkingImg,
  },
];
